// src/components/ordering/WarehouseSelector.tsx
import { useState, useEffect } from "react";
import { Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  WarehouseLocationsService,
  Warehouse,
} from "@/services/warehouse-locations.service";

interface WarehouseSelectorProps {
  value: string | null;
  onChange: (id: string | null) => void;
  disabled?: boolean;
}

const NONE = "__none__";

export function WarehouseSelector({
  value,
  onChange,
  disabled,
}: WarehouseSelectorProps) {
  const [warehouses, setWarehouses] = useState<Warehouse[]>([]);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState("");
  const [adding, setAdding] = useState(false);

  useEffect(() => {
    void WarehouseLocationsService.list().then(setWarehouses);
  }, []);

  async function handleAdd() {
    const trimmed = newName.trim();
    if (!trimmed) return;
    setAdding(true);
    const created = await WarehouseLocationsService.create(trimmed);
    if (created) {
      setWarehouses((prev) => [...prev, created]);
      onChange(created.id);
      setNewName("");
      setShowAdd(false);
    }
    setAdding(false);
  }

  return (
    <div className="space-y-1.5">
      <Select
        value={value ?? NONE}
        onValueChange={(v) => onChange(v === NONE ? null : v)}
        disabled={disabled}
      >
        <SelectTrigger className="h-8 text-xs">
          <SelectValue placeholder="No warehouse assigned" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value={NONE} className="text-xs text-muted-foreground">
            No warehouse assigned
          </SelectItem>
          {warehouses.map((w) => (
            <SelectItem key={w.id} value={w.id} className="text-xs">
              {w.name}
              {w.code ? ` (${w.code})` : ""}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {!showAdd ? (
        <button
          type="button"
          className="flex items-center gap-1 text-[10px] text-muted-foreground hover:text-foreground"
          onClick={() => setShowAdd(true)}
        >
          <Plus className="h-2.5 w-2.5" /> Add new warehouse
        </button>
      ) : (
        <div className="flex gap-1.5">
          <Input
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            placeholder="e.g. Naples"
            className="h-7 text-xs flex-1"
            autoFocus
            onKeyDown={(e) => {
              if (e.key === "Enter") void handleAdd();
              if (e.key === "Escape") {
                setShowAdd(false);
                setNewName("");
              }
            }}
          />
          <Button
            type="button"
            size="sm"
            className="h-7 text-xs px-2"
            disabled={adding || !newName.trim()}
            onClick={() => void handleAdd()}
          >
            {adding ? "…" : "Add"}
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            className="h-7 text-xs px-2"
            onClick={() => {
              setShowAdd(false);
              setNewName("");
            }}
          >
            Cancel
          </Button>
        </div>
      )}
    </div>
  );
}
